<template>
  <div v-if="processedCitation.length" class="a-object-citation">
    <template v-if="citationHeader || useHeaderIcon">
      <div class="a-object-citation-header">
        <div
          v-if="citationHeader"
          class="a-object-citation-header__item-header"
        >
          {{ citationHeader }}
        </div>
        <CopyButton
          v-if="useHeaderIcon"
          :text="setCitationText"
          text-key="copy-citation-header"
          :icon-size="24"
          name="copy-citation"
          @copied-text="copyCitation"
        />
      </div>
    </template>

    <p v-if="citationPrompt">
      {{ citationPrompt }}
    </p>

    <div class="a-object-citation__container">
      <div class="a-object-citation__container_inner">
        <div
          ref="citation"
          :class="{
            'a-object-citation__citation': true,
            'a-object-citation__citation--copied': isCopied
          }"
        >
          <div class="a-object-citation__citation_text">
            <DelimitedFieldValues
              v-for="(field, index) in processedCitation"
              :key="index"
              :values="field"
              delimiter=", "
              data-testid="openContentCitation"
            />
          </div>
          <CopyButton
            v-if="!useHeaderIcon"
            :text="setCitationText"
            text-key="copy-citation-container"
            :icon-size="24"
            class="a-object-citation__button"
            name="copy-citation"
            @copied-text="copyCitation"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { CopyButton, DelimitedFieldValues } from "@thegetty-private/getty-ui";
import { useLocalization } from "@thegetty-private/getty-ui";
const UNKNOWN = "Unknown";
const FIELD_KEY = ".citation";

export default {
  name: "ObjectCitation",
  components: { CopyButton, DelimitedFieldValues },

  props: {
    /**
     * Citation object
     */
    citationFields: {
      type: Object,
      required: true
    },
    /**
     * Whether to use a copy icon in the header
     */
    useHeaderIcon: {
      type: Boolean,
      default: false
    },
    /**
     * Header Text for the Citation
     */
    citationHeader: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * Additional Prompt Text for the Citation
     */
    citationPrompt: {
      type: String,
      required: false,
      default: undefined
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },

  data() {
    return {
      /**
       * Copy button default value
       */
      copyButtonValue: this.translatePart("object_citation.copy_button"),
      /**
       * Copy button copied value
       */
      copyButtonCopiedValue: this.translatePart(
        "object_citation.copy_button_copied"
      ),
      /**
       * If user has clicked copy gui button
       */
      isCopied: false
    };
  },
  computed: {
    /**
     * Formatted artists citation display string
     * Concatenates prefix, name, and suffix if necessary
     * Handles 1, 2, and 3+ artists
     *
     * * @returns {string} - a string of artist names
     */
    citationArtists() {
      const conjunction = this.translatePart("object_citation.conjunction");
      const etal = this.translatePart("object_citation.et_al");
      let citationArtists = this.citationFields[FIELD_KEY].artists;
      if (citationArtists == undefined) {
        return citationArtists;
      }

      citationArtists = citationArtists.map((artist) =>
        artist == undefined || artist == null ? UNKNOWN : artist
      );

      if (!Array.isArray(citationArtists)) {
        return citationArtists;
      }

      let formattedArtists = [];

      citationArtists.forEach(function (artist) {
        let nationality_and_dates = artist.nationality_and_dates
          ? `(${artist.nationality_and_dates})`
          : "";

        let prefix =
          artist.prefix && artist.prefix.trim().includes("and")
            ? ""
            : artist.prefix;

        // replace prefixes that are duplicated in name field if present
        let concatArtist = [
          prefix,
          artist.name,
          artist.suffix,
          nationality_and_dates
        ].join(" ");
        if (
          Array.isArray(artist.role) &&
          artist.role.length > 0 &&
          artist.role[0].toLowerCase() != "artist"
        ) {
          let role = artist.role[0].toLowerCase();

          concatArtist = `${concatArtist.trim()}, ${role}`;
        }

        concatArtist = concatArtist.replace(/  /g, " ");
        formattedArtists.push(concatArtist.trim());
      });

      if (
        formattedArtists.length == 1 &&
        formattedArtists[0] == UNKNOWN &&
        this.citationFields[FIELD_KEY].culture != undefined
      ) {
        formattedArtists[0] += ` (${this.citationFields[FIELD_KEY].culture})`;
      }

      if (formattedArtists.length < 3) {
        return formattedArtists.join(` ${conjunction} `);
      }

      // 3 or more
      formattedArtists[2] = ` ${formattedArtists[2]}`;
      return `${formattedArtists.slice(0, 3).join(", ")}, ${etal}`;
    },
    /**
     * Copy button value
     *
     * @returns {string} - translation value for the text copy button
     */
    getCopyButtonValue() {
      let value = this.isCopied
        ? this.copyButtonCopiedValue
        : this.copyButtonValue;
      return value;
    },
    /**
     * Processed citation text fields
     * @returns {array} - of citation field values
     */
    processedCitation() {
      let citation;
      if (!this.citationFields[FIELD_KEY]) {
        return [];
      } else {
        citation = this.citationFields[FIELD_KEY];
      }

      if (Array.isArray(citation.dimensions) == false) {
        citation.dimensions = [citation.dimensions];
      }
      // citation fields arranged according to modified Chicago citation style
      // see DEV-12969 for current caption formatting and style guidelines
      return [
        this.citationArtists,
        [
          {
            name: citation.title,
            cssClasses: ["a-object-citation__citation--title"]
          },
          citation.date
        ],
        [citation.medium],
        // request from Brenda/Cherie to separate Dimensions onto it's own line DEV-10437 for better support of multiple dimensions
        citation.dimensions,
        [citation.credit_line, citation.object_number]
      ];
    }
  },
  methods: {
    /**
     * Method passed to CopyButton component so that we can use DelimitedFieldValues
     * component's reference ID to generate the citation text.
     *
     * @returns {string} | {undefined} - the text string for the CopyButton
     */
    setCitationText() {
      if (this.$refs.citation && this.$refs.citation.innerText) {
        return this.$refs.citation.innerText.replaceAll("\n\n", "\n");
      }
      return undefined;
    },
    /**
     * Handle copy button click
     */
    copyCitation() {
      // user feedback on copy
      this.isCopied = !this.isCopied;
      setTimeout(() => {
        this.isCopied = !this.isCopied;
      }, 1500);
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
